import {ContainerModule, ContainerModuleLoadOptions} from 'inversify'
import {PageOptionsFactory} from '@/src/core/domain/page/page-options'
import {DI} from '../types'
import {PageType} from '@/src/core/domain/entities/page'
import qs from 'qs'
import {LoadPageDataParams} from '@/src/core/domain/page'
import {Routes} from '@/src/config/routes'
import {getRouteParam} from '@/src/shared/utils/route-options-helpers'
import {isWeb} from '@/src/shared/utils/functions'
import {DEFAULT_REVALIDATE_SECONDS, settings} from '@/src/config/settings'
import castArray from 'lodash/castArray'

const staticPageGuard = (
  params: any,
  pageType: ValuesOf<typeof PageType>,
): params is {path: string} => params && pageType === PageType.Static

const initializeModule = ({bind}: ContainerModuleLoadOptions) => {
  bind<PageOptionsFactory>(DI.PageOptionsFactory).toFactory(
    () =>
      <Params extends Record<any, any>, SearchParams extends Record<any, any>>(
        pageType: ValuesOf<typeof PageType>,
        pageParams: MaybePromise<Params>,
        pageSearchParams: URLSearchParams | MaybePromise<SearchParams>,
      ): (() => Promise<LoadPageDataParams<Params, SearchParams>>) => {
        return async () => {
          try {
            const params = await pageParams
            const _searchParams = await pageSearchParams

            const searchParams = (
              _searchParams instanceof URLSearchParams
                ? qs.parse(_searchParams.toString())
                : qs.parse(qs.stringify(_searchParams))
            ) as SearchParams

            if (staticPageGuard(params, pageType)) {
              const path = getRouteParam('slug', params, searchParams)
                ? `/${castArray(getRouteParam('slug', params, searchParams) ?? []).join('/')}`
                : getRouteParam('path', params, searchParams).replace(/(\/.*)(\/)$/, '$1')

              params.path = path

              return {
                type: PageType.Static,
                params: isWeb() ? params : ({} as Params),
                searchParams: isWeb() ? searchParams : params,
                container: {limit: false, padding: false},
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                static: {
                  meta: {
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${params.path}/`,
                    },
                  },
                },
              }
            } else if (pageType === PageType.Home) {
              return {
                type: PageType.Home,
                params,
                searchParams,
                container: {
                  limit: false,
                  padding: false,
                },
                static: {
                  meta: {
                    alternates: {
                      canonical: settings.NEXT_PUBLIC_FRONTEND_URL,
                    },
                  },
                },
                revalidate: DEFAULT_REVALIDATE_SECONDS,
              }
            } else if (pageType === PageType.BugReport) {
              return {
                type: PageType.BugReport,
                params,
                searchParams,
                static: {
                  meta: {
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.bugReport.page}`,
                    },
                  },
                },
              }
            } else if (pageType === PageType.CatalogRoot) {
              let canonical = `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.catalog.page}`

              const __slug = getRouteParam('slug', params, searchParams)

              if (__slug) {
                pageType = PageType.CatalogProductList

                if (isWeb()) {
                  canonical += `${castArray(__slug).join('/')}/`
                }
              }

              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'catalog',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                static: {
                  meta: {
                    alternates: {
                      canonical,
                    },
                  },
                },
              }
            } else if (pageType == PageType.LoginPage) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'login',
                container: {
                  center: true,
                },
                static: {
                  meta: {
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.auth.login.page}`,
                    },
                  },
                },
              }
            } else if (pageType == PageType.ProfileRoot) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile_root',

                static: {
                  meta: {
                    title: 'Личный кабинет',
                    description: 'Личный кабинет',
                  },
                  breadcrumbs: [{title: 'Личный кабинет'}],
                },
                container: {title: 'Личный кабинет'},
              }
            } else if (pageType == PageType.Profile) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile',
                static: {
                  content: {tab: 'personal'},
                  meta: {
                    title: 'Личный кабинет',
                    description: 'Личный кабинет',
                  },
                  breadcrumbs: [
                    {title: 'Личный кабинет', url: Routes.profile.page},
                    {title: 'Профиль'},
                  ],
                },
                container: {title: 'Личный кабинет'},
              }
            } else if (pageType == PageType.ProfileBonusAccount) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile',
                static: {
                  content: {tab: 'bonus-card'},
                  meta: {
                    title: 'Бонусная карта',
                  },
                  breadcrumbs: [
                    {title: 'Личный кабинет', url: Routes.profile.page},
                    {title: 'Бонусная карта'},
                  ],
                },
                container: {title: 'Бонусная карта'},
              }
            } else if (pageType == PageType.ProfileFavourite) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile-favourite',
                static: {
                  content: {tab: 'favourite'},
                  meta: {
                    title: 'Избранное',
                  },
                  breadcrumbs: [
                    {title: 'Личный кабинет', url: Routes.profile.page},
                    {title: 'Избранное'},
                  ],
                },
                container: {title: 'Избранное'},
              }
            } else if (pageType == PageType.ProfileOrderList) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile',
                static: {
                  content: {tab: 'orders'},
                  meta: {
                    title: 'Мои заказы',
                  },
                  breadcrumbs: [
                    {title: 'Личный кабинет', url: Routes.profile.page},
                    {title: 'Мои заказы'},
                  ],
                },
                container: {
                  title: 'Мои заказы',
                },
              }
            } else if (pageType == PageType.ProfileServiceRecords) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'profile',
                static: {
                  content: {tab: 'service-records'},
                  meta: {
                    title: 'Мои записи',
                  },
                  breadcrumbs: [
                    {title: 'Личный кабинет', url: Routes.profile.page},
                    {title: 'Мои записи'},
                  ],
                },
                container: {
                  title: 'Мои записи',
                },
              }
            } else if (pageType == PageType.ArticlesList) {
              const __slug = getRouteParam('slug', params, searchParams)
              const __path = getRouteParam('path', params, searchParams)

              const meta: PageMeta = {}

              if (__slug || __path) {
                const path = __slug
                  ? `${(castArray(__slug) ?? []).join('/')}`
                  : __path.replace(/(\/.*)(\/)$/, '$1')
                meta['alternates'] = {
                  canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${__slug ? Routes.articles.section.page(path) : Routes.articles.page}`,
                }
              } else {
                meta['alternates'] = {
                  canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.articles.page}`,
                }
              }

              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'articles',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                container: {
                  theme: 'dark',
                },
                static: {
                  meta,
                },
              }
            } else if (pageType == PageType.ArticleDetail) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'articles',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                static: {
                  meta: {
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.articles.article.page(getRouteParam('code', params, searchParams))}`,
                    },
                  },
                },
              }
            } else if (pageType == PageType.Brands) {
              let pageTypeOptions: Partial<LoadPageDataParams> = {}

              const __slug = getRouteParam('slug', params, searchParams)

              if (__slug) {
                pageTypeOptions = {
                  type: PageType.BrandProductList,
                  static: {
                    breadcrumbs: [{title: 'Бренды', url: Routes.brands.page}],
                    meta: {
                      alternates: {
                        canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.brands.detail(__slug)}`,
                      },
                    },
                  },
                }
              } else {
                pageTypeOptions = {
                  ...pageTypeOptions,
                  static: {
                    breadcrumbs: [{title: 'Бренды'}],
                    meta: {
                      alternates: {
                        canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.brands.page}`,
                      },
                    },
                  },
                  container: {
                    title: 'Бренды',
                  },
                }
              }

              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'brands',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                ...pageTypeOptions,
              }
            } else if (pageType == PageType.Cart) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'cart',
                static: {
                  breadcrumbs: [{title: 'Корзина'}],
                  meta: {
                    'empty-cart': 'Digi Empty Cart Page',
                    title: 'Корзина',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.basket.page}`,
                    },
                  },
                },
              }
            } else if (pageType == PageType.Certificates) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'certificates',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                static: {
                  breadcrumbs: [{title: 'Сертификаты'}],
                  meta: {
                    title: 'Сертификаты',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.certificates.page}`,
                    },
                  },
                },
              }
            } else if (pageType == PageType.Contacts) {
              return {
                type: pageType,
                params,
                searchParams,
                static: {
                  breadcrumbs: [{title: 'Контакты'}],
                  meta: {
                    title: 'КОНТАКТЫ',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.contacts.page}`,
                    },
                  },
                },
                container: {title: 'Контакты'},
                revalidate: DEFAULT_REVALIDATE_SECONDS,
              }
            } else if (pageType == PageType.Favourite) {
              return {
                type: pageType,
                params,
                searchParams,
                container: {title: 'Избранное'},
                static: {
                  meta: {
                    title: 'Мои избранные товары',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.favourite.page}`,
                    },
                  },
                  breadcrumbs: [{title: 'Избранное'}],
                },
                transformKey: 'favourite',
              }
            } else if (pageType == PageType.NewsList) {
              let pageTypeOptions: Partial<LoadPageDataParams> = {}
              const __code = getRouteParam('code', params, searchParams)

              if (__code) {
                pageTypeOptions = {
                  ...pageTypeOptions,
                  type: PageType.NewsDetail,
                  static: {
                    meta: {
                      alternates: {
                        canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.news.detail.page(__code)}`,
                      },
                    },
                  },
                }
              } else {
                pageTypeOptions = {
                  ...pageTypeOptions,
                  container: {theme: 'dark'},
                  static: {
                    meta: {
                      alternates: {
                        canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.news.page}`,
                      },
                    },
                  },
                }
              }

              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'news',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                ...pageTypeOptions,
              }
            } else if (pageType == PageType.Order) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'order',
                container: {title: 'Оформление заказа'},
                static: {
                  breadcrumbs: [{title: 'Оформление заказа'}],
                  meta: {
                    title: 'Оформление заказа',
                  },
                },
                onNotFound: 'redirect-to-cart',
              }
            } else if (pageType == PageType.SuccessOrder) {
              return {
                type: pageType,
                params,
                searchParams,
                static: {
                  meta: {title: 'Заказ создан'},
                  breadcrumbs: [],
                },
              }
            } else if (pageType == PageType.CatalogProduct) {
              function isMobileRouteParams(
                params: Record<any, any>,
                searchParams: Record<any, any>,
              ): params is {code: string; path?: string} {
                return getRouteParam('path', params, searchParams)
              }

              if (isMobileRouteParams(params, searchParams)) {
                delete params.path
                params.code = getRouteParam('path', params, searchParams)
              }

              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'catalog',
                static: {
                  meta: {
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.catalog.product.page(params.code, 'simple')}`,
                    },
                  },
                },
                revalidate: DEFAULT_REVALIDATE_SECONDS,
              }
            } else if (pageType == PageType.FunctionalTesting) {
              return {
                type: pageType,
                params,
                searchParams,
                static: {
                  meta: {
                    title: 'Запись на функциональное тестирование',
                    description: 'Запись на функциональное тестирование',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.service.functionalTesting}`,
                    },
                  },
                  breadcrumbs: [{title: 'Запись на функциональное тестирование'}],
                },
                container: {
                  title: 'Запись на функциональное тестирование',
                },
                transformKey: 'func-testing',
              }
            } else if (pageType == PageType.ShoeTesting) {
              return {
                type: pageType,
                params,
                searchParams,
                static: {
                  meta: {
                    title: 'Запись на подбор обуви',
                    description: 'Запись на подбор обуви',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.service.shoeTesting}`,
                    },
                  },
                  breadcrumbs: [{title: 'Запись на подбор обуви'}],
                },
                container: {
                  title: 'Запись на подбор обуви',
                },
                transformKey: 'shoe-testing',
              }
            } else if (pageType == PageType.VideoReviewList) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'video',
                revalidate: DEFAULT_REVALIDATE_SECONDS,
                static: {
                  meta: {
                    title: 'Запись на функциональное тестирование',
                    description: 'Запись на функциональное тестирование',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.video.page}`,
                    },
                  },
                  breadcrumbs: [{title: 'Видеообзоры'}],
                },
                container: {
                  theme: 'dark',
                  title: 'Видеообзоры',
                },
              }
            } else if (pageType == PageType.CatalogSales) {
              return {
                type: pageType,
                params,
                searchParams,
                transformKey: 'catalog',
                static: {
                  meta: {
                    title: 'Распродажа, скидки',
                    description: 'Распродажа, скидки',
                    alternates: {
                      canonical: `${settings.NEXT_PUBLIC_FRONTEND_URL}${Routes.catalog.sale.page}`,
                    },
                  },
                  breadcrumbs: [{title: 'SALE'}],
                },
                revalidate: DEFAULT_REVALIDATE_SECONDS,
              }
            }

            return {
              params,
              searchParams,
              type: PageType.NotFound,
            }
          } catch (e: unknown) {
            console.error(e)
            throw e
          }
        }
      },
  )
}

export const PageOptionsModule = new ContainerModule(initializeModule)
